<template>
	<a
		target="_blank"
		:rel="linkRel"
		:href="offerUrl"
		data-allow-mismatch="attribute"
		@click.capture="onClickout($event)"
	>
		<slot />
	</a>
</template>

<script setup lang="ts">
import type { UrlHelper } from '@/helpers/clickout-helper';
import type { ClickoutType } from '@/interfaces/snowplow/clickout-context';

import { computed } from 'vue';

import { handleClickoutUrl, isClickoutUrl, trackClickout } from '@/helpers/clickout-helper';

const {
	href,
	rel = 'noopener',
	skipPreAffiliationCheck,
	clickoutType,
} = defineProps<{
	/** URl of the offer, should be a ClickoutURL link. */
	href: UrlHelper;

	/** Value for the link's `rel` attribute */
	rel?: string;

	/** Whether to skip the pre-affiliation check */
	skipPreAffiliationCheck?: boolean;

	/** Clickout Type */
	clickoutType?: ClickoutType;
}>();

const emits = defineEmits<{ (e: 'click', event: MouseEvent): void }>();

const enableSkipUct = computed(() => false);

const linkRel = computed(() =>
	isClickoutUrl(href) && href.provider?.package?.maskReferrer ? `${rel ?? ''} noreferrer` : rel
);

const triggerRecompute = ref(0);

const offerUrl = computed(() => {
	triggerRecompute.value;

	if (enableSkipUct.value) {
		if (isClickoutUrl(href) && href.preAffiliatedUrl) {
			return href.preAffiliatedUrl;
		}
		return href.get('r') ?? href.toString();
	}

	// return the directly affiliated link
	return href.toString();
});

const client = useNuxtApp().$apolloClients.default!;

function onClickout(event: MouseEvent) {
	emits('click', event);

	if (!enableSkipUct.value) {
		if (isClickoutUrl(href)) {
			trackClickout(href);
			triggerRecompute.value++;
		}
		return;
	}

	// Skip UCT is active, prevent default <a> behavior and take over with our `handleClickoutUrl` logic
	event.preventDefault();

	handleClickoutUrl(href, client, enableSkipUct.value, !skipPreAffiliationCheck);
}
</script>
